import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { AssignmentsService, BasicUser } from '../../../../../js/api';
import { Button } from '../../../components/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '../../../components/dialog';
import { Field, Fieldset } from '../../../components/fieldset';
import { Input } from '../../../components/input';

interface ManagerPickerModalProps {
  open: boolean;
  onClose: () => void;
  assignmentId: string;
  healthcareEntityId: string;
  currentManagerId?: string | null;
  currentUser?: { id?: string; first_name?: string; last_name?: string } | null;
  onSuccess: (updated: { managerId: string | null; displayName: string | null }) => void;
}

export const ManagerPickerModal: React.FC<ManagerPickerModalProps> = ({
  open,
  onClose,
  assignmentId,
  healthcareEntityId,
  currentManagerId,
  currentUser,
  onSuccess,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: managers, isLoading } = useQuery({
    queryKey: ['assignmentEligibleManagers', healthcareEntityId],
    queryFn: () =>
      AssignmentsService.assignmentsAssignmentEligibleManagersList({
        healthcareEntity: healthcareEntityId,
      }),
    enabled: open && !!healthcareEntityId,
  });

  const filteredManagers = useMemo(() => {
    if (!managers) return [];
    const term = searchTerm.trim().toLowerCase();
    if (!term) return managers;
    return managers.filter((m) => {
      const name = `${m.first_name || ''} ${m.last_name || ''}`.toLowerCase();
      return name.includes(term) || (m.email || '').toLowerCase().includes(term);
    });
  }, [managers, searchTerm]);

  const mutation = useMutation({
    mutationFn: (managedBy: string | null) =>
      AssignmentsService.assignmentsAssignmentManagerUpdatePartialUpdate({
        id: assignmentId,
        requestBody: { managed_by: managedBy },
      }),
  });

  const submit = (managedBy: string | null, displayName: string | null, successMessage: string) => {
    mutation.mutate(managedBy, {
      onSuccess: () => {
        toast.success(successMessage);
        onSuccess({ managerId: managedBy, displayName });
        setSearchTerm('');
        setSelectedId(null);
        onClose();
      },
      onError: () => {
        toast.error('Could not update the assignment manager. Please try again.');
      },
    });
  };

  const handleAssignToMe = () => {
    if (!currentUser?.id) return;
    const displayName =
      `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || null;
    submit(currentUser.id, displayName, 'Assigned to you');
  };

  const handleUnassign = () => {
    submit(null, null, 'Unassigned');
  };

  const handleContinue = () => {
    const picked = (managers || []).find((m: BasicUser) => m.id === selectedId);
    if (!picked) return;
    const displayName = `${picked.first_name || ''} ${picked.last_name || ''}`.trim() || null;
    submit(picked.id || null, displayName, `Assigned to ${displayName || 'user'}`);
  };

  return (
    <Dialog open={open} onClose={onClose} className="tw:!p-3">
      <DialogTitle className="tw:!text-base tw:!font-semibold tw:!mb-1 tw:p-2 tw:rounded-lg tw:bg-brand-green-0 tw:!text-white">
        Assign Manager
      </DialogTitle>
      <DialogBody className="tw:!mt-0">
        <Fieldset>
          <DialogDescription>Choose who is responsible for this assignment.</DialogDescription>
          <div className="tw:flex tw:gap-2 tw:!mt-3 tw:!mb-3">
            {currentUser?.id && (
              <Button
                color="brand-green-outline"
                onClick={handleAssignToMe}
                disabled={mutation.isLoading}
              >
                Assign to me
              </Button>
            )}
            {currentManagerId && (
              <Button plain onClick={handleUnassign} disabled={mutation.isLoading}>
                Unassign
              </Button>
            )}
          </div>
          <Field>
            <Input
              type="text"
              placeholder="Search by name or email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Field>
          <div className="tw:!mt-2 tw:max-h-64 tw:overflow-y-auto tw:border tw:border-zinc-200 tw:rounded">
            {isLoading && <p className="tw:!p-3 tw:text-sm tw:text-zinc-500 tw:!mb-0">Loading…</p>}
            {!isLoading && filteredManagers.length === 0 && (
              <p className="tw:!p-3 tw:text-sm tw:text-zinc-500 tw:!mb-0">No matching users.</p>
            )}
            {!isLoading &&
              filteredManagers.map((m: BasicUser) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setSelectedId(m.id || null)}
                  className={`tw:w-full tw:text-left tw:!p-2 tw:!border-0 tw:border-b tw:border-zinc-100 tw:cursor-pointer tw:flex tw:flex-col ${
                    selectedId === m.id ? 'tw:bg-brand-green-3' : 'tw:bg-white hover:tw:bg-zinc-50'
                  }`}
                >
                  <span className="tw:text-sm tw:font-medium tw:text-zinc-900">
                    {m.first_name} {m.last_name}
                  </span>
                  <span className="tw:text-xs tw:text-zinc-500">{m.email}</span>
                </button>
              ))}
          </div>
        </Fieldset>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={mutation.isLoading}>
          Cancel
        </Button>
        <Button
          color="brandgreen0"
          disabled={!selectedId || mutation.isLoading}
          onClick={handleContinue}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};
